import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { ISchemaSensor } from '@/interfaces/entities/schema_sensor'
import type { IEnergySensor, IDoorOpenSensor, ITemperatureSensor } from '@/interfaces/entities/sensor'

export type ISensorWithSchema = IEnergySensor | ITemperatureSensor | IDoorOpenSensor

export interface ISensorSchemaFieldMeta {
  labelKey: string
  unit: string
}

const LABEL_PREFIX = 'custom_dashboards.sensor_schema_fields'

export const SENSOR_SCHEMA_FIELD_MAP: Record<string, ISensorSchemaFieldMeta> = {
  current_a: { labelKey: `${LABEL_PREFIX}.current_a`, unit: 'A' },
  current_b: { labelKey: `${LABEL_PREFIX}.current_b`, unit: 'A' },
  current_c: { labelKey: `${LABEL_PREFIX}.current_c`, unit: 'A' },
  voltage_a: { labelKey: `${LABEL_PREFIX}.voltage_a`, unit: 'V' },
  voltage_b: { labelKey: `${LABEL_PREFIX}.voltage_b`, unit: 'V' },
  voltage_c: { labelKey: `${LABEL_PREFIX}.voltage_c`, unit: 'V' },
  voltage_ab: { labelKey: `${LABEL_PREFIX}.voltage_ab`, unit: 'V' },
  voltage_bc: { labelKey: `${LABEL_PREFIX}.voltage_bc`, unit: 'V' },
  voltage_ca: { labelKey: `${LABEL_PREFIX}.voltage_ca`, unit: 'V' },
  voltage_abc_average: { labelKey: `${LABEL_PREFIX}.voltage_abc_average`, unit: 'V' },
  power_factor: { labelKey: `${LABEL_PREFIX}.power_factor`, unit: '' },
  power_factor_a: { labelKey: `${LABEL_PREFIX}.power_factor_a`, unit: '' },
  power_factor_b: { labelKey: `${LABEL_PREFIX}.power_factor_b`, unit: '' },
  power_factor_c: { labelKey: `${LABEL_PREFIX}.power_factor_c`, unit: '' },
  power_active: { labelKey: `${LABEL_PREFIX}.power_active`, unit: 'kW' },
  power_active_a: { labelKey: `${LABEL_PREFIX}.power_active_a`, unit: 'kW' },
  power_active_b: { labelKey: `${LABEL_PREFIX}.power_active_b`, unit: 'kW' },
  power_active_c: { labelKey: `${LABEL_PREFIX}.power_active_c`, unit: 'kW' },
  power_reactive: { labelKey: `${LABEL_PREFIX}.power_reactive`, unit: 'kVAr' },
  power_reactive_a: { labelKey: `${LABEL_PREFIX}.power_reactive_a`, unit: 'kVAr' },
  power_reactive_b: { labelKey: `${LABEL_PREFIX}.power_reactive_b`, unit: 'kVAr' },
  power_reactive_c: { labelKey: `${LABEL_PREFIX}.power_reactive_c`, unit: 'kVAr' },
  power_apparent: { labelKey: `${LABEL_PREFIX}.power_apparent`, unit: 'kVA' },
  power_apparent_a: { labelKey: `${LABEL_PREFIX}.power_apparent_a`, unit: 'kVA' },
  power_apparent_b: { labelKey: `${LABEL_PREFIX}.power_apparent_b`, unit: 'kVA' },
  power_apparent_c: { labelKey: `${LABEL_PREFIX}.power_apparent_c`, unit: 'kVA' },
  consumption: { labelKey: `${LABEL_PREFIX}.consumption`, unit: 'kWh' },
  consumption_a: { labelKey: `${LABEL_PREFIX}.consumption_a`, unit: 'kWh' },
  consumption_b: { labelKey: `${LABEL_PREFIX}.consumption_b`, unit: 'kWh' },
  consumption_c: { labelKey: `${LABEL_PREFIX}.consumption_c`, unit: 'kWh' },
  consumption_reactive: { labelKey: `${LABEL_PREFIX}.consumption_reactive`, unit: 'kVArh' },
  consumption_reactive_a: { labelKey: `${LABEL_PREFIX}.consumption_reactive_a`, unit: 'kVArh' },
  consumption_reactive_b: { labelKey: `${LABEL_PREFIX}.consumption_reactive_b`, unit: 'kVArh' },
  consumption_reactive_c: { labelKey: `${LABEL_PREFIX}.consumption_reactive_c`, unit: 'kVArh' },
  frequency: { labelKey: `${LABEL_PREFIX}.frequency`, unit: 'Hz' },
  temperature: { labelKey: `${LABEL_PREFIX}.temperature`, unit: '°C' },
  door_open_voltage_field: { labelKey: `${LABEL_PREFIX}.door_open_voltage_field`, unit: 'V' },
  voltage_field: { labelKey: `${LABEL_PREFIX}.voltage_field`, unit: 'V' },
}

const SCHEMA_KEY_ALIASES: Record<string, string> = {
  voltage_field: 'door_open_voltage_field',
}

/** Mapeia chave lógica do schema para a chave usada em `readings` da API de registros. */
const READINGS_KEY_BY_DATA_FIELD: Record<string, string> = {
  door_open_voltage_field: 'door_open_voltage',
  voltage_field: 'door_open_voltage',
}

export function getReadingsKeyForDataField(dataField: string): string {
  return READINGS_KEY_BY_DATA_FIELD[dataField] ?? dataField
}

function normalizeSchemaKey(key: string): string {
  return SCHEMA_KEY_ALIASES[key] ?? key
}

function humanizeSchemaKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

export function getSchemaFieldLabel(
  key: string,
  t: (path: string) => string,
  te?: (path: string) => boolean,
): string {
  const normalized = normalizeSchemaKey(key)
  const meta = SENSOR_SCHEMA_FIELD_MAP[normalized] ?? SENSOR_SCHEMA_FIELD_MAP[key]
  if (meta) {
    const path = meta.labelKey
    if (!te || te(path)) {
      return t(path)
    }
  }
  return humanizeSchemaKey(key)
}

export function getSchemaFieldUnit(key: string): string {
  if (!key) return ''
  const normalized = normalizeSchemaKey(key)
  const meta = SENSOR_SCHEMA_FIELD_MAP[normalized] ?? SENSOR_SCHEMA_FIELD_MAP[key]
  return meta?.unit ?? ''
}

export function resolveWidgetDataFields(config: IWidget['config']): string[] {
  if (config.data_fields?.length) return config.data_fields
  if (config.data_field) return [config.data_field]
  return []
}

export function buildPieChartSliceKey(sensorId: string, dataField: string): string {
  return `sensor:${sensorId}:${dataField}`
}

export function buildSensorSchemaFieldOptionsFromKeys(
  keys: string[],
  t: (path: string) => string,
  te?: (path: string) => boolean,
): ISensorSchemaFieldOption[] {
  return keys.map((value) => ({
    value,
    label: getSchemaFieldLabel(value, t, te),
  }))
}

export function intersectSensorSchemaFieldKeys(
  sensorsDetails: Array<ISensorWithSchema | null>,
): string[] {
  const keySets = sensorsDetails
    .filter((details): details is ISensorWithSchema => details != null)
    .map((details) => new Set(getSensorSchemaFieldKeys(details)))

  if (keySets.length === 0) return []

  return [...keySets[0]].filter((key) => keySets.every((set) => set.has(key)))
}

/** Unidade de exibição por `data_field`: prioriza o map do schema; senão usa fallback (ex.: API). */
export function resolveDataFieldUnit(dataField: string, fallbackUnit = ''): string {
  if (!dataField) return fallbackUnit

  const mappedUnit = getSchemaFieldUnit(dataField)
  if (mappedUnit) return mappedUnit

  const hasKnownField = Boolean(
    SENSOR_SCHEMA_FIELD_MAP[dataField] ?? SENSOR_SCHEMA_FIELD_MAP[normalizeSchemaKey(dataField)],
  )
  if (hasKnownField) return ''

  return fallbackUnit
}

/** Unidade de exibição do widget: prioriza o map por `data_field`; senão usa fallback (ex.: API). */
export function resolveWidgetUnit(widget: IWidget, fallbackUnit = ''): string {
  const dataField = widget.config.data_field ?? widget.config.data_fields?.[0]
  return resolveDataFieldUnit(dataField ?? '', fallbackUnit)
}

export function getSensorSchemaFieldKeys(sensorDetails: ISensorWithSchema | null): string[] {
  const schema = sensorDetails?.schema_sensor?.schema
  if (!schema) return []

  return Object.entries(schema)
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([key]) => key)
}

export interface ISensorSchemaFieldOption {
  value: string
  label: string
}

export function buildSensorSchemaFieldOptions(
  sensorDetails: ISensorWithSchema | null,
  t: (path: string) => string,
  te?: (path: string) => boolean,
): ISensorSchemaFieldOption[] {
  return getSensorSchemaFieldKeys(sensorDetails).map((value) => ({
    value,
    label: getSchemaFieldLabel(value, t, te),
  }))
}

export function getSchemaFromSensorDetails(
  sensorDetails: ISensorWithSchema | null,
): ISchemaSensor['schema'] | null {
  return sensorDetails?.schema_sensor?.schema ?? null
}
